import { useEffect, useState } from 'react';
import { Button } from '../../../components/Button';
import styled from 'styled-components';
import { LocationsOptions } from '../../dashboard/pages/activate';
import { useCreateCustomerSecretMutation } from '../../subscription/api/subscriptionApi';
import { FullScreenSpinner } from '../../../components/Spinner/Spinner';
import { useSendPricingTableDataMutation, useCheckSolarSimMutation } from '../../dashboard/api/activateApi';

type PricingSectionProps = {
  email: string;
  iccid: string;
  reservationId?: string;
  customerId?: string;
  reactivation?: boolean;
  location: LocationsOptions;
  title?: string;
  description?: string;
};

export const PricingSection = ({
  email,
  iccid,
  reservationId,
  customerId,
  reactivation = false,
  title,
  description,
  location
}: PricingSectionProps) => {
  const checkoutReference = reservationId || iccid;
  const isUk = location === 'uk';
  const [customerSession, setCustomerSession] = useState<{
    customerId: string; iccid: string; secret?: string; error?: string;
  }>();
  const [secretAttempt, setSecretAttempt] = useState(0);
  // A secret from another SIM/account must never appear during a route change.
  const currentSession = customerSession?.customerId === customerId && customerSession?.iccid === iccid
    ? customerSession : undefined;
  const customerSecret = currentSession?.secret;
  const [pricingTableId, setPricingTableId] = useState<string>('');
  const [isSolarSim, setIsSolarSim] = useState<boolean>(false);
  const [createCustomerSecret, { isLoading }] = useCreateCustomerSecretMutation();
  const [sendPricingData] = useSendPricingTableDataMutation();
  const [checkSolarSim, { isLoading: checkingSolarSim }] = useCheckSolarSimMutation();

  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://js.stripe.com/v3/pricing-table.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, [iccid, email]);

  // Check if the SIM is a solar SIM and set the appropriate pricing table
  useEffect(() => {
    const checkSolarSimStatus = async () => {
      try {
        const result = await checkSolarSim({ iccid, location }).unwrap();
        setIsSolarSim(result.is_solar_sim);
        setPricingTableId(result.pricing_table_id);
      } catch (error) {
        console.error('Error checking solar SIM status:', error);
        // Fallback to regular pricing tables
        const fallbackTableId = isUk
          ? 'prctbl_1QWfOrLUMGsj0i84qRWdybZF'
          : 'prctbl_1O6z8YLUMGsj0i84B8mi30oD';
        setPricingTableId(fallbackTableId);
        setIsSolarSim(false);
      }
    };

    if (iccid) {
      checkSolarSimStatus();
    }
  }, [iccid, location, isUk, checkSolarSim]);

  useEffect(() => {
    if (!customerId) return;
    let current = true;
    setCustomerSession(undefined);
    createCustomerSecret({ customerId }).unwrap()
      .then(data => {
        if (!current) return;
        if (typeof data.secret !== 'string' || !data.secret.trim()) {
          throw new Error('Missing customer session');
        }
        setCustomerSession({ customerId, iccid, secret: data.secret });
      })
      .catch(() => {
        if (current) setCustomerSession({ customerId, iccid,
          error: 'We couldn’t connect checkout to your existing account. Please try again or contact support.' });
      });
    return () => { current = false; };
  }, [customerId, iccid, secretAttempt, createCustomerSecret]);

  useEffect(() => {
    sendPricingData({
      'client-reference-id': checkoutReference,
      'customer-email': email,
      'customer-session-client-secret': customerSecret
    });
  }, [checkoutReference, email, customerSecret]);

  // Missing renewal ownership must never become first activation.
  if (reactivation && !customerId) return (
    <Container role="alert">
      <Description>We couldn’t find your existing billing account. Please return to your dashboard or contact support.</Description>
    </Container>
  );
  // Renewal must fail closed: email-only Checkout can create another customer.
  if (customerId && currentSession?.error) return (
    <Container role="alert">
      <Description>{currentSession.error}</Description>
      <Button onClick={() => { setCustomerSession(undefined); setSecretAttempt(attempt => attempt + 1); }}>
        Try again
      </Button>
    </Container>
  );
  if (isLoading || checkingSolarSim || !pricingTableId || (customerId && !customerSecret)) {
    return <FullScreenSpinner />;
  }

  return (
    <Container>
      <Title>Please check your details before proceeding.</Title>
      <Description center>
        SIM: <Bold>{iccid}</Bold>
      </Description>
      <Description center style={{ marginBottom: '2rem' }}>
        E-mail: <Bold>{email}</Bold>
      </Description>
      <Title>{title || 'Choose Your Plan'}</Title>

      <Description>
        {description ||
          'Choose between Monthly or Yearly payments below - save with annual plans. You can upgrade or downgrade your plan at any time.'}
      </Description>
      <StripWrapper>
        {/* @ts-ignore */}
        <stripe-pricing-table
          pricing-table-id={pricingTableId}
          publishable-key="pk_live_51NSIcfLUMGsj0i84eNlhV0h0NzkZ46yBvoGhksQ9tFfX3NOIsIRMuiOZA3mpS5ifw2ivN6URgo0GPwOp6Xndrzyv00ojRywbNv"
          client-reference-id={checkoutReference}
          customer-email={customerSecret ? undefined : email}
          customer-session-client-secret={customerSecret} //FROM RENEW SUBSCRIPTION
        />
      </StripWrapper>
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
`;

const StripWrapper = styled.div`
  padding: 1rem;
  background-color: rgb(229, 249, 255);
  border-radius: 0.4rem;
`;

const Bold = styled.span`
  font-weight: bold;
`;

const Description = styled.p<{ center?: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.small};
  margin-bottom: 0.5rem;
  text-align: ${({ center }) => (center ? 'center' : 'left')};
`;

const Title = styled.h3`
  margin: 0;
  text-align: center;
  margin-bottom: 1.2rem;
`;
